// ============================================================================
// VersionHistoryPanel.jsx — Shows project snapshots, lets user restore any
// ============================================================================

import { useState, useEffect } from 'react'

const API_URL = import.meta.env.VITE_API_URL || ''

export default function VersionHistoryPanel({ projectId, userEmail, onClose, onRestore }) {
  const [versions, setVersions] = useState([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [newLabel, setNewLabel] = useState('')

  useEffect(() => {
    if (!projectId || !userEmail) return
    fetchVersions()
  }, [projectId, userEmail])

  const fetchVersions = async () => {
    try {
      setLoading(true)
      const res = await fetch(`${API_URL}/projects/${projectId}/versions?email=${encodeURIComponent(userEmail)}`)
      const data = await res.json()
      setVersions(data.versions || [])
    } catch (e) {
      console.error('Load versions error:', e)
    } finally {
      setLoading(false)
    }
  }

  const handleSnapshot = async () => {
    if (!projectId) return
    setSaving(true)
    try {
      const res = await fetch(`${API_URL}/projects/${projectId}/snapshot`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: userEmail, label: newLabel || undefined })
      })
      if (res.ok) {
        setNewLabel('')
        await fetchVersions()
      }
    } catch (e) {
      console.error('Snapshot error:', e)
    } finally {
      setSaving(false)
    }
  }

  const handleRestore = async (versionId) => {
    if (!confirm('Restore this version? Your current state will be saved as a new version first.')) return
    try {
      const res = await fetch(`${API_URL}/projects/${projectId}/restore/${versionId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: userEmail })
      })
      if (res.ok) {
        if (onRestore) onRestore()
        onClose()
      }
    } catch (e) {
      console.error('Restore error:', e)
    }
  }

  const timeAgo = (date) => {
    const s = Math.floor((Date.now() - new Date(date)) / 1000)
    if (s < 60) return 'just now'
    if (s < 3600) return `${Math.floor(s / 60)}m ago`
    if (s < 86400) return `${Math.floor(s / 3600)}h ago`
    return `${Math.floor(s / 86400)}d ago`
  }

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-slate-800 rounded-2xl shadow-2xl max-w-md w-full max-h-[80vh] overflow-hidden border border-slate-700 flex flex-col" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="p-4 border-b border-slate-700 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-white">Version History</h2>
            <p className="text-xs text-slate-500">Last 10 snapshots — oldest auto-deleted</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white text-xl">✕</button>
        </div>

        {/* Save current snapshot */}
        <div className="p-4 border-b border-slate-700">
          <div className="flex gap-2">
            <input
              type="text"
              value={newLabel}
              onChange={e => setNewLabel(e.target.value)}
              placeholder="Label for this snapshot (optional)"
              className="flex-1 px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-sm text-white placeholder-slate-500 focus:outline-none focus:border-zenna-violet"
            />
            <button
              onClick={handleSnapshot}
              disabled={saving}
              className="px-4 py-2 bg-gradient-to-r from-zenna-violet to-zenna-cyan text-white text-sm font-medium rounded-lg hover:opacity-90 disabled:opacity-40 transition"
            >
              {saving ? '...' : 'Save'}
            </button>
          </div>
        </div>

        {/* Versions list */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin w-6 h-6 border-2 border-zenna-violet border-t-transparent rounded-full" />
            </div>
          ) : versions.length === 0 ? (
            <div className="text-center py-8 text-slate-500 text-sm">
              <p className="mb-2">No snapshots yet</p>
              <p className="text-xs">Save a snapshot before making big changes — you can always restore it later!</p>
            </div>
          ) : (
            <div className="space-y-2">
              {versions.map((v, i) => (
                <div key={v.id} className="flex items-center justify-between p-3 bg-slate-900 rounded-lg hover:bg-slate-900/80 transition">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-white truncate">{v.label}</span>
                      {i === 0 && <span className="text-[9px] bg-emerald-500/20 text-emerald-400 px-1.5 py-0.5 rounded-full">Latest</span>}
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">{timeAgo(v.timestamp)}</div>
                  </div>
                  <button
                    onClick={() => handleRestore(v.id)}
                    className="px-3 py-1.5 text-xs bg-zenna-violet/20 text-zenna-violet rounded-lg hover:bg-zenna-violet/30 transition ml-2"
                  >
                    Restore
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
